import { format } from "date-fns";
import type {
  CurrencySymbolPositionOption,
  DataFormatValues,
  NullDisplayOption,
  ZeroDisplayOption,
} from "./types";

const EM_DASH = "\u2014";
const EN_DASH = "\u2013";

/** Sample base value for the top number preview: negative one hundred
 * thousand renders as `($100,000.00)` under the default settings. */
export const SAMPLE_NUMBER = -100_000;

/** Sample date for the date preview: `June 01, 2026` under the default
 * `MMMM dd, yyyy` pattern. A Monday, so weekday examples read
 * "Monday"/"Mon"; the day must be < 10 so the builder's `dd`=01 vs `d`=1
 * dropdown labels differ. Local-time so the preview and tests read the
 * same year/month/day regardless of TZ. */
export const SAMPLE_DATE = new Date(2026, 5, 1);

const buildCurrencySymbol = (show: boolean, position: CurrencySymbolPositionOption) => {
  if (!show) return { left: "", right: "" };
  switch (position) {
    case "left":
      return { left: "$", right: "" };
    case "left_space":
      return { left: "$ ", right: "" };
    case "right":
      return { left: "", right: "$" };
    case "right_space":
      return { left: "", right: " $" };
    default:
      return { left: "$", right: "" };
  }
};

export const formatNumberPreview = (
  values: Pick<
    DataFormatValues,
    | "negativeNumbers"
    | "numberScale"
    | "thousandsSeparator"
    | "showCurrencySymbol"
    | "currencySymbolPosition"
    | "decimalPlaces"
  >,
  sample: number = SAMPLE_NUMBER,
): string => {
  let scaled = sample;
  let scaleSuffix = "";
  if (values.numberScale === "thousands") {
    scaled = sample / 1_000;
    scaleSuffix = "K";
  }

  const isNegative = scaled < 0;
  const magnitude = Math.abs(scaled);

  const decimals = Math.max(0, Math.min(20, values.decimalPlaces));
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: values.thousandsSeparator,
  });
  const numberStr = formatter.format(magnitude) + scaleSuffix;

  const { left, right } = buildCurrencySymbol(
    values.showCurrencySymbol,
    values.currencySymbolPosition,
  );
  const withCurrency = `${left}${numberStr}${right}`;

  if (!isNegative) return withCurrency;
  switch (values.negativeNumbers) {
    case "parentheses":
      return `(${withCurrency})`;
    case "minus":
      return `-${withCurrency}`;
    case "absolute":
      return withCurrency;
    default:
      return withCurrency;
  }
};

export const formatZeroPreview = (option: ZeroDisplayOption): string => {
  switch (option) {
    case "number":
      return "0";
    case "em_dash":
      return EM_DASH;
    case "en_dash":
      return EN_DASH;
    case "blank":
      return "";
    case "na":
      return "N/A";
    default:
      return "";
  }
};

export const formatNullPreview = (option: NullDisplayOption): string => {
  switch (option) {
    case "number":
      return "0";
    case "blank":
      return "";
    case "em_dash":
      return EM_DASH;
    case "en_dash":
      return EN_DASH;
    case "na":
      return "N/A";
    default:
      return "";
  }
};

/** Render the sample date with a raw date-fns pattern. Returns an empty
 * string for an empty pattern. The builder only emits valid patterns, but
 * the pattern is backend-persisted data — if a stored value uses a token
 * this date-fns build rejects (e.g. the BE allowlist grows first), `format`
 * throws; fall back to an empty preview instead of crashing the panel. */
export const formatDatePreview = (pattern: string, sample: Date = SAMPLE_DATE): string => {
  if (!pattern) return "";
  try {
    return format(sample, pattern);
  } catch {
    return "";
  }
};
